import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { forgetTenantId } from '../services/tenant'
import { listActivity, listTenants, type Tenant, type TenantActivity } from '../services/superadmin'
import { TenantTable } from '../components/superadmin/TenantTable'
import { TenantForm } from '../components/superadmin/TenantForm'
import { CodesPanel } from '../components/superadmin/CodesPanel'
import { LeadsPanel } from '../components/superadmin/LeadsPanel'
import { SubscriptionsPanel } from '../components/superadmin/SubscriptionsPanel'
import { FinancePanel } from '../components/superadmin/FinancePanel'
import { Spinner } from '../components/Spinner'
import { EyeIcon, KeyRoundIcon, XIcon, LogOutIcon, PhoneIcon, RotateCwIcon, StoreIcon, TrendingUpIcon, WalletIcon } from '../components/icons'
import './SuperadminPage.css'

/**
 * The system administrator's screen: edit institutions already open, issue
 * activation codes, follow up call requests.
 *
 * Deliberately its own layout, not POSLayout, POSLayout's header and menu
 * are built around a single tenant's context, and this screen has none. It
 * is reached by signing in on the ordinary login screen with the system
 * administrator's address; a visitor who is not signed in is sent there.
 */
export function SuperadminPage() {
  const { isAuthenticated, isSuperadmin, loading: authLoading, logout, switchTenant } = useAuth()
  const navigate = useNavigate()
  const [tenants, setTenants] = useState<Tenant[]>([])
  /** Kurumların son görülmesi; yüklenemezse liste yine görünür. */
  const [activity, setActivity] = useState<Record<string, TenantActivity>>({})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<'tenants' | 'codes' | 'leads' | 'subs' | 'fin'>('tenants')
  /** Yenile'ye basıldı, sayfa yeniden yükleniyor. */
  const [refreshing, setRefreshing] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listTenants()
      setTenants(list)
      listActivity()
        .then(setActivity)
        .catch(() => setActivity({}))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'İşletmeler yüklenemedi.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && isSuperadmin) reload()
  }, [isAuthenticated, isSuperadmin, reload])

  // Kurum paneli Esc ile kapanır.
  useEffect(() => {
    if (!selectedId) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelectedId(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId])

  if (authLoading) return null
  // The one login screen serves everyone, the system administrator included:
  // it reads the account and sends them back here once they are in.
  if (!isAuthenticated) return <Navigate to="/giris" replace />

  // A signed-in visitor who is not the system administrator, a cashier who
  // found the URL, say. No redirect target is named, same as landing on any
  // other route they cannot use.
  if (!isSuperadmin) {
    return (
      <div className="superadmin superadmin--login">
        <p className="superadmin__empty">Bu sayfaya erişiminiz yok.</p>
      </div>
    )
  }

  const selected = tenants.find((t) => t.id === selectedId) ?? null

  return (
    <div className="superadmin">
      <header className="superadmin__header">
        <img className="superadmin__avatar" src="/icon-192.png" alt="" />
        <div className="superadmin__title">
          <span className="superadmin__eyebrow">Adissoo</span>
          <h1>Sistem Yönetimi</h1>
        </div>
        {/* Solda logo, sağda yalnızca çıkış simgesi (kullanıcı, 11 Eki 2026).
            Çıkış giriş ekranına götürüyor ve hatırlanan kurumu unutuyor: sistem yöneticisi
            bir kuruma ait değil, sonraki giriş kendi kurumunu adresten ya da
            kimlikten bulmalı. */}
        {/* Yenile (kullanıcı, 21 Eki 2026): panel kasa düzenini kullanmıyor,
            aşağı çekip yenileme burada yok. En yeni sürümü alıp baştan yükler. */}
        <button
          type="button"
          className={`superadmin__logout superadmin__refresh ${refreshing ? 'is-on' : ''}`}
          aria-label="Sayfayı yenile"
          title="Sayfayı yenile"
          disabled={refreshing}
          onClick={() => {
            setRefreshing(true)
            if (window.__adissooRefresh) void window.__adissooRefresh(600)
            else window.location.reload()
          }}
        >
          <RotateCwIcon />
        </button>
        <button
          type="button"
          className="superadmin__logout"
          aria-label="Çıkış yap"
          title="Çıkış yap"
          onClick={async () => {
            await logout()
            forgetTenantId()
            navigate('/giris', { replace: true })
          }}
        >
          <LogOutIcon />
        </button>
      </header>

      {/* Two jobs that share nothing but the person doing them: institutions
          already open, and the codes that open new ones. */}
      <nav className="superadmin__tabs">
        {(
          [
            ['tenants', 'İşletmeler', <StoreIcon key="i" />],
            ['codes', 'Aktivasyon kodları', <KeyRoundIcon key="i" />],
            ['leads', 'Arama talepleri', <PhoneIcon key="i" />],
            ['subs', 'Abonelikler', <WalletIcon key="i" />],
            ['fin', 'Gelir-Gider', <TrendingUpIcon key="i" />],
          ] as const
        ).map(([id, label, icon]) => (
          <button
            key={id}
            type="button"
            className={`superadmin__tab ${tab === id ? 'is-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {error && <p className="superadmin__error">{error}</p>}

      {tab === 'codes' && <CodesPanel />}
      {tab === 'leads' && <LeadsPanel />}
      {tab === 'fin' && <FinancePanel />}
      {tab === 'subs' && (loading ? <Spinner label="Yükleniyor…" /> : <SubscriptionsPanel tenants={tenants} />)}

      {/* Kurumlar tablosu (31 Eki 2026): tam genişlik, satır satır, her
          sütunda filtre. Seçilen kurumun ayarları sağdan açılan panelde. */}
      {tab === 'tenants' && (
        <section className="superadmin__panel">
          <div className="superadmin__panel-head">
            <h2>İşletmeler</h2>
            <span className="superadmin__count">{tenants.length}</span>
          </div>

          {loading ? (
            <Spinner label="Yükleniyor…" />
          ) : (
            <TenantTable
              tenants={tenants}
              activity={activity}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </section>
      )}

      {tab === 'tenants' && selected && (
        <TenantWindow
          tenant={selected}
          onClose={() => setSelectedId(null)}
          onSaved={reload}
          onView={() => {
            switchTenant(selected.id)
            navigate(`/${selected.id}/ana`)
          }}
        />
      )}
    </div>
  )
}

/**
 * İşletme penceresi (2 Kas 2026, kullanıcı: "yarım sayfa açılmasın tam
 * ekranda açılsın"): çekmeceyle aynı kart, kenardan 12 px içeride, tam ekran.
 * Ayrı bileşen, yerel önizleme (/dev/isletme) de bunu açıyor.
 */
export function TenantWindow({
  tenant,
  onClose,
  onSaved,
  onView,
}: {
  tenant: Tenant
  onClose: () => void
  onSaved: () => void
  onView: () => void
}) {
  return (
    <div className="tdrawer tdrawer--full" role="dialog" aria-modal="true" aria-label={tenant.display_name}>
      <div className="tdrawer__backdrop" onClick={onClose} />
      <aside className="tdrawer__panel">
        <header className="tdrawer__head">
          <div>
            <h2>{tenant.display_name}</h2>
            <span>{tenant.id}</span>
          </div>
          {/* Görüntüleme modu (kullanıcı, 21 Eki 2026): kurumun ekranlarını
              patron gibi görür, hiçbir şeyi değiştiremez, kurumda iz kalmaz. */}
          <button
            type="button"
            className="btn btn--primary tdrawer__view"
            aria-label="İşletmeyi görüntüle"
            title="Patronun gördüğü ekranlar, salt okunur. İşletmede iz kalmaz."
            onClick={onView}
          >
            <EyeIcon />
            <i className="btn__rule" />
            <span>İşletmeyi görüntüle</span>
          </button>
          <button type="button" className="tdrawer__x" aria-label="Kapat" onClick={onClose}>
            <XIcon />
          </button>
        </header>

        <div className="tdrawer__body">
          <TenantForm key={tenant.id} tenant={tenant} onSaved={onSaved} />
        </div>
      </aside>
    </div>
  )
}
